/**
 * @engine/core - Engine Core
 *
 * Central integration point for all engine systems.
 * Uses GameObject/Component architecture (like Unity).
 *
 * Core concepts:
 * - GameObject: Container for components with transform hierarchy
 * - Component: Behavior/data attached to GameObjects (written in AS)
 * - Level: Collection of GameObjects (renamed from Scene)
 * - Engine: Lifecycle management and system orchestration
 *
 * Uses:
 * - @engine/vfs for file system
 * - @engine/aid for asset database
 * - @engine/render2d for rendering
 * - @engine/math for math operations
 *
 * @module @engine/core
 */

// ============================================================================
// CORE TYPES
// ============================================================================

export type {
  // Identifiers
  GameObjectId,
  ComponentId,
  LevelId,
  GUID,

  // GameObject
  GameObjectConfig,
  GameObjectState,

  // Component
  ComponentConfig,
  ComponentState,
  ComponentLifecycle,

  // Transform
  TransformData,

  // Tags & Layers
  Tag,
  Layer,

  // Events
  CoreEventType,
  CoreEventPayloads,
} from "./types";

export {
  generateGUID,
  NULL_GUID,
  DEFAULT_TRANSFORM,
  Tags,
  Layers,
} from "./types";

// ============================================================================
// GAME OBJECT
// ============================================================================

export { GameObject, resetIdCounters } from "./GameObject";
export { Transform } from "./Transform";

// ============================================================================
// COMPONENT TYPE MAP & FACTORY
// ============================================================================

export type { ComponentTypeMap, ComponentTypeId } from "./ComponentTypeMap";
export { createComponentInstance } from "./ComponentTypeMap";

// ============================================================================
// SCRIPTING CONTRACTS (IScriptExecutor / IEngineContext / Behaviour)
// ============================================================================

export * from "./scripting";

// ============================================================================
// COMPONENT TOKEN TABLE (reflection-free getComponent)
// ============================================================================

export { Components, COMPONENT_COUNT } from "./ComponentTable.generated";
export { ct, tokenForId, tokenForTypeId, allTokens } from "./ComponentTable";

// ============================================================================
// COMPONENT CLASSES
// ============================================================================

export {
  AudioListener,
  AudioSource,
  BoxCollider2D,
  BuoyancyZone2D,
  Camera2D,
  CameraRig,
  PositionControlMode,
  RotationControlMode,
  StandbyUpdateMode,
  BlendHint,
  CapsuleCollider2D,
  CircleCollider2D,
  ConvexCollider2D,
  GravityZone2D,
  PolygonCollider2D,
  RigidBody2D,
  Spline2D,
  SplineEvaluator,
  createSplineEvaluator,
  SpriteRenderer,
  SurfaceEffector2D,
  TargetGroup,
  evaluateTargetGroup,
  TransformComponent,
  UICanvas,
  UIScaleMode,
  VFXEmitter,
  WindZone2D,
} from "./components";

export type {
  SplineType,
  ResolvedSplinePoint,
} from "./components";

// ============================================================================
// TRANSFORM STORAGE (SoA)
// ============================================================================

export type {
  TransformHandle,
  TransformStorageConfig,
  TransformStorageSnapshot,
  WorldTransform,
} from "./TransformStorage";
export {
  INVALID_HANDLE,
  TransformStorage,
  getTransformStorage,
  resetTransformStorage,
} from "./TransformStorage";

// ============================================================================
// LEVEL
// ============================================================================

export type { LevelConfig, LevelState, EditorFolder } from "./Level";
export { Level, resetLevelIdCounter } from "./Level";
export { SceneEvents } from "./SceneEvents";
export type { ObjectEvent, ComponentEvent, ObjectListener, ComponentListener } from "./SceneEvents";
export { SceneEventsSubsystem } from "./SceneEventsSubsystem";

export { LevelManager, LoadLevelMode, levelManager, LevelEvents } from "./LevelManager";
export type { LevelLoadRequest, LevelLoaderHost, LevelEventMap } from "./LevelManager";

// ============================================================================
// LEVEL DEPENDENCIES (shared asset-ref extraction) + RESOURCE MANAGEMENT
// ============================================================================

export {
  getAssetReferenceFields,
  resetAssetReferenceFields,
  extractAssetRefsFromSerializedGO,
  extractAssetRefsFromSerializedLevel,
} from "./LevelDependencies";

export { ResourceManager } from "./resources/ResourceManager";
export type { AssetKind, ResourceBackends } from "./resources/ResourceManager";
export type { LevelDependencyProvider } from "./resources/LevelDependencyProvider";

// ============================================================================
// LEVEL SERIALIZATION
// ============================================================================

export type {
  SerializedComponent,
  SerializedGameObject,
  SerializedLevel,
  SerializedEditorFolder,
} from "./LevelSerializer";
export {
  LEVEL_FORMAT_VERSION,
  serializeGameObject,
  serializeLevel,
  deserializeGameObject,
  deserializeLevel,
  cloneGameObject,
  levelToJSON,
  levelFromJSON,
} from "./LevelSerializer";

// ============================================================================
// ENGINE LIFECYCLE
// ============================================================================

export type { EngineConfig, EngineState, ISystem } from "./Engine";
export { Engine, engine } from "./Engine";

export type { LoopCallbacks } from "./GameLoop";
export { GameLoop, LoopState } from "./GameLoop";

export type { TimeState } from "./Time";
export { Time } from "./Time";

// ============================================================================
// APPLICATION LIFECYCLE
// ============================================================================

export type {
  SubsystemConfig,
  AppEvents,
  ISubsystem,
  IApplication,
} from "./Application";
export {
  AppState,
  SubsystemPriority,
  Application,
  Subsystem,
} from "./Application";

// ============================================================================
// COMPONENT REGISTRY
// ============================================================================

export type {
  FieldType,
  FieldDef,
  ComponentDef,
  ComponentData,
} from "./ComponentRegistry";

export { ComponentRegistry, componentRegistry } from "./ComponentRegistry";

// ============================================================================
// COMPONENT DECORATORS
// ============================================================================

export {
  // Class decorators
  component,
  displayName,
  icon,
  color,
  category,
  requireComponent,
  executeInEditor,
  allowMultiple,
  customEditor,
  // Field decorator
  field,
  // Runtime-only-state marker
  runtime,
  // Utilities
  getComponentMetadata,
  getFieldMetadata,
  getFieldDefs,
  getCustomEditorName,
  fieldMetadataToFieldDef,
  getAllComponents,
  getComponentByTypeId,
  componentRegistry as componentDecoratorRegistry,
  COMPONENT_METADATA,
  // Runtime init
  runtimeInit,
} from "./attributes";

export type {
  FieldOptions,
  FieldMetadata,
  ComponentMetadata,
} from "./attributes";

// ============================================================================
// DIAGNOSTICS
// ============================================================================

export type { DiagnosticsData } from "./Diagnostics";
export { Diagnostics, diagnostics } from "./Diagnostics";

// ============================================================================
// SPRITE RENDERER
// ============================================================================

export type {
  SpriteHandle,
  SpriteRendererData,
  SpriteBatchEntry,
  SpriteRendererStorageConfig,
} from "./SpriteRendererStorage";

export {
  INVALID_SPRITE_HANDLE,
  DEFAULT_COLOR,
  SpriteRendererStorage,
  getSpriteRendererStorage,
  resetSpriteRendererStorage,
} from "./SpriteRendererStorage";

// ============================================================================
// CAMERA
// ============================================================================

export {
  type CameraInstance,
  type PostProcessStackData,
  type PostProcessEffectData,
  type ISerializableEffect,
} from "./Camera";

export {
  SpriteRenderSystem,
  type TextureResolver,
  type SpriteRenderSystemConfig,
  type ISpriteBatch,
  type ICamera2D,
  type IRenderContext as ICoreRenderContext,
  type TextureHandle,
} from "./SpriteRenderSystem";

// ============================================================================
// PROFILER
// ============================================================================

export type {
  ProfileSample,
  ProfileFrame,
  MemorySnapshot,
  ProfilerStats,
} from "./Profiler";

export { Profiler, ProfileScope, profile, profileAsync } from "./Profiler";

// ============================================================================
// ENGINE NODES (Redprint)
// ============================================================================

export {
  ENGINE_NODES,
  LIFECYCLE_NODES,
  TRANSFORM_NODES,
  GAMEOBJECT_NODES,
  SPRITE_NODES,
  PHYSICS_NODES,
  AUDIO_NODES,
  registerCustomType,
  getCustomType,
  getAllCustomTypes,
  isCustomType,
  registerBuiltinCustomTypes,
  initializeEngineNodes,
  resetEngineNodes,
} from "./nodes";

// ============================================================================
// COLOUR UTILS
// ============================================================================

export { packColorABGR } from './ColorUtils';
export type { RGBA } from './ColorUtils';

// ============================================================================
// SPRITE SYNC (Level → SoA storages bridge)
// ============================================================================

export {
  getSpriteAssetId,
  getActiveSpriteAssetIds,
  getSpriteHandle,
  syncSpritesToStorage,
  clearSpriteSync,
} from './SpriteSync';

// ============================================================================
// VIEWPORT (shared renderer infrastructure)
// ============================================================================

export type {
  ViewportCameraState,
  ViewportRenderRequest,
  ViewportRenderResult,
  IViewportRenderer,
  RenderStats,
  CollectedSprite,
  SpriteCollectorDeps,
  ForwardLitParams,
  ViewportPassContext,
  LightingResources,
  ViewportRenderCallbacks,
} from './viewport';

export {
  FPSLimiter,
  BaseViewportRenderer,
  GameViewRenderer,
  SceneSpriteCollector,
  RenderPipeline,
  ViewportPass,
  CallbackPass,
  RenderStage,
  BB,
  CollectLightsPass,
  InjectVfxLightsPass,
  CullLightsPass,
  ComputeShadowsPass,
  GBufferOpaquePass,
  DeferredAccumPass,
  ForwardPlusOpaquePass,
  UnlitOpaquePass,
  TransparentSpritesPass,
  ParticlesPass,
  PostProcessPass,
  screenToWorld,
  worldToScreen,
} from './viewport';

// ============================================================================
// LIGHTING (portable light / shadow collection from Level)
// ============================================================================

export type { LightCollectStats } from './LightCollector';
export { collectLightsFromLevel } from './LightCollector';
export { collectShadowCastersFromLevel } from './ShadowCollector';

// ============================================================================
// CAMERA RIG SYSTEM
// ============================================================================

export type {
  CameraRigOutput,
  CameraRigSystemOptions,
} from "./CameraRigSystem";

export {
  CameraRigSystem,
  cameraRigSystem,
  updateCameraRigs,
  createCameraRigEngineSystem,
} from "./CameraRigSystem";

// ============================================================================
// EMBEDDED ASSETS
// ============================================================================

export type { EmbeddedAssetEntry } from './assets/EmbeddedAssets';
export { embeddedAssets } from './assets/EmbeddedAssets';
